import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import Input from './shared/Input';
import './ChooseUsername.css';

const ChooseUsername = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Redirect if not authenticated
    if (!authService.isAuthenticated()) {
      navigate('/');
      return;
    }

    // Redirect if username already confirmed
    const user = authService.getUser();
    if (user && user.usernameConfirmed) {
      navigate('/play');
      return;
    }

    // Pre-fill with suggestion for guests
    if (authService.isGuest()) {
      const suggestion = `Guest_${Math.random().toString(36).substr(2, 6)}`;
      setUsername(suggestion);
    }
  }, [navigate]);

  const validateUsername = (name) => {
    if (!name || name.trim().length === 0) {
      return 'Username is required';
    }
    if (name.length < 3) {
      return 'Username must be at least 3 characters';
    }
    if (name.length > 20) {
      return 'Username must be 20 characters or less';
    }
    if (!/^[a-zA-Z0-9\s\-_]+$/.test(name)) {
      return 'Username can only contain letters, numbers, spaces, hyphens, and underscores';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validateUsername(username);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      // Check if user is guest or OAuth
      const isGuest = authService.isGuest();

      if (isGuest) {
        // Guest user: Update username locally
        const success = authService.updateGuestUsername(username.trim());

        if (!success) {
          throw new Error('Failed to update username');
        }

        navigate('/play');
      } else {
        // OAuth user: Update username via backend API
        const token = authService.getToken();
        const API_URL =
          process.env.REACT_APP_API_URL ||
          process.env.REACT_APP_BACKEND_URL ||
          (process.env.NODE_ENV === 'production'
            ? window.location.origin
            : 'http://localhost:3001');
        const response = await fetch(`${API_URL}/auth/update-username`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ username: username.trim() }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to update username');
        }

        const data = await response.json();

        // Update local user data
        const user = authService.getUser();
        user.username = data.user.username;
        user.usernameConfirmed = true;
        localStorage.setItem('user', JSON.stringify(user));

        // CRITICAL: Also update the old system 'username' key that the game uses
        localStorage.setItem('username', data.user.username);

        // Redirect to play page
        navigate('/play');
      }
    } catch (err) {
      console.error('[ChooseUsername] Error:', err);
      setError(err.message || 'Failed to update username. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="choose-username-page">
      <div className="choose-username-container">
        <h1 className="choose-username-title">Choose Your Display Name</h1>
        <p className="choose-username-subtitle">
          This is what other players will see during games.
        </p>

        <form onSubmit={handleSubmit}>
          <Input
            type="text"
            placeholder="Enter your display name"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            maxLength={20}
            disabled={isSubmitting}
            autoFocus
            error={error}
            showCharCount
            id="username-input"
            name="username"
            ariaLabel="Choose your display name"
          />

          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting || !username.trim()}
          >
            {isSubmitting ? 'Saving...' : 'Continue'}
          </button>
        </form>

        <div className="privacy-reminder">
          <p>
            <strong>Privacy tip:</strong> Avoid using your real name if you want to stay anonymous.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChooseUsername;
